import { useState, type ReactNode } from 'react';
import type { PhoneTab } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { TabBar } from './TabBar.js';

/**
 * Il guscio del telefono: marchio in alto, contenuto in mezzo, cinque schede in basso.
 *
 * ## Tre fasce, e solo quella in mezzo scorre
 *
 * È la differenza che si sente in mano. Facendo scorrere tutta la pagina, la barra delle schede
 * scivolerebbe via appena si scende — e su un elenco di duecento canali tornare indietro
 * vorrebbe dire risalire tutto. Qui l'altezza è fissa, e l'unico pezzo con `overflow` è il mezzo.
 *
 * ## `100dvh` e non `100vh`
 *
 * Su un telefono la barra degli indirizzi si ritira scorrendo, e `vh` resta fermo all'altezza
 * **massima**: con `100vh` le schede finiscono sotto il bordo dello schermo finché non si scorre,
 * cioè proprio all'apertura. `dvh` segue l'altezza vera.
 *
 * ## La fascia dell'ambiente di test non c'è, e non è una dimenticanza
 *
 * `docs/04-SCHERMATE-MOBILE.md` §2 la chiede anche qui. Non c'è perché **non esiste nemmeno sulla
 * TV**: nessuno in questa applicazione sa oggi dire se l'ambiente è di prova, e il dato non arriva
 * da nessuna parte. Aggiungere qui una proprietà che nessuno passa sarebbe una funzione dichiarata e
 * mai in vigore — il contrario di averla. Va messa in tutti e due i gusci insieme, il giorno che
 * quel segnale esiste.
 */
export function PhoneShell({
  activeTab,
  onPickTab,
  profileName = null,
  accountLine = null,
  onSignOut,
  hideChrome = false,
  children,
}: {
  /** La scheda accesa, o `null` dove nessuna lo è: lo decide `tabFor` nel dominio. */
  readonly activeTab: PhoneTab | null;
  readonly onPickTab: (tab: PhoneTab) => void;
  readonly profileName?: string | null;
  /** `ambiente · dominio`, come lo mostra il prototipo sotto il nome. */
  readonly accountLine?: string | null;
  readonly onSignOut: () => void;
  /**
   * Nasconde marchio e schede, lasciando allo schermo solo il contenuto.
   *
   * Serve a una cosa sola: un canale a tutto schermo. Non si smonta niente e non si sposta niente
   * nell'albero — sarebbe un rimontaggio, cioè il canale che riparte, che è il difetto appena
   * corretto. Si tolgono due fasce, e quel che c'è in mezzo si prende lo spazio.
   */
  readonly hideChrome?: boolean;
  readonly children: ReactNode;
}) {
  const translator = useTranslator();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      data-phone-shell="1"
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: colors.background,
        color: colors.text,
        font: `500 15px ${FONT_STACK}`,
      }}
    >
      <header
        style={{
          /* A schermo intero le due fasce spariscono: resta il video e basta. */
          display: hideChrome ? 'none' : 'flex',
          flex: 'none',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          /*
            `env(safe-area-inset-top)`: sui telefoni con l'intaglio, il marchio ci finisce sotto.
            Vale lo stesso ragionamento del margine in basso nella barra delle schede.
          */
          padding: `calc(12px + env(safe-area-inset-top, 0px)) 20px 12px`,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 3,
            font: `800 26px ${FONT_STACK}`,
            letterSpacing: '-.03em',
          }}
        >
          palco
          <span
            aria-hidden
            style={{ width: 7, height: 7, borderRadius: 4, background: colors.accent }}
          />
        </span>

        <button
          type="button"
          onClick={() => setMenuOpen((before) => !before)}
          aria-expanded={menuOpen}
          aria-label={translator.t('phone.account.open')}
          style={{
            // 44 px: sotto, il dito sbaglia. È il bersaglio più piccolo di tutta questa fascia.
            width: 44,
            height: 44,
            border: 0,
            borderRadius: 22,
            background: colors.surfaceRaised,
            color: colors.text,
            font: `700 14px ${FONT_STACK}`,
            cursor: 'pointer',
          }}
        >
          {/*
            L'iniziale del profilo, o un segnaposto. Non si mostra il nome per esteso: su 390 px
            ruberebbe la riga al marchio, e un nome lungo la prenderebbe tutta.
          */}
          {(profileName ?? '').trim().slice(0, 1).toUpperCase() || '·'}
        </button>
      </header>

      {menuOpen && (
        <div
          style={{
            flex: 'none',
            margin: '0 20px 12px',
            padding: '14px 16px',
            borderRadius: 14,
            background: colors.surfaceRaised,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {profileName !== null && profileName !== '' && (
              <span style={{ font: `700 15px ${FONT_STACK}` }}>{profileName}</span>
            )}
            {accountLine !== null && accountLine !== '' && (
              <span style={{ font: `500 12px ${FONT_STACK}`, color: colors.textDim }}>
                {accountLine}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              height: 44,
              border: 0,
              borderRadius: 10,
              background: colors.errorPanel,
              color: colors.errorText,
              font: `700 14px ${FONT_STACK}`,
              cursor: 'pointer',
            }}
          >
            {translator.t('phone.account.signOut')}
          </button>
        </div>
      )}

      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          /*
            **Colonna flessibile, e non è una preferenza di stile: senza, il video è alto zero.**

            Le schermate sono scritte per un genitore flessibile — la radice della diretta dice
            `flex: 1`, come sul televisore, dove il contenitore è una riga flessibile e funziona. Qui
            `main` era un blocco ordinario, quindi quel `flex: 1` non valeva niente: l'altezza della
            schermata diventava quella del suo contenuto, e il suo contenuto è **tutto posizionato in
            modo assoluto**. Zero.

            Il risultato era un canale con l'audio e lo schermo nero, con l'intestazione e le schede
            al loro posto — un sintomo che somiglia in tutto a un codec che il telefono non sa
            decodificare, e che ha mandato mezza giornata dietro all'H.265 e a un decoder da scrivere.
            Misurato in Chrome: `video = 500x0` prima, `500x724` dopo.

            `flex-shrink` non schiaccia gli elenchi lunghi: un elemento flessibile ha `min-height:
            auto`, quindi non scende sotto il proprio contenuto, e lo scorrimento resta quello di
            prima. Misurato anche questo, su sessanta righe.
          */
          display: 'flex',
          flexDirection: 'column',
          /*
            `touch` e non `auto`: su iOS senza questo lo scorrimento per inerzia non c'è, e la
            differenza si sente subito — un elenco che si ferma di colpo sembra un'app lenta.
          */
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </main>

      {!hideChrome && <TabBar active={activeTab} onPick={onPickTab} />}
    </div>
  );
}
